import { action, makeObservable, observable, runInAction } from 'mobx';

/**
 * UXA-9 (concept-design-agency §24.2): an Approve or a Request changes is held for a few seconds with
 * Undo before it is sent. Nothing goes before the window closes: Undo inside it sends nothing, and the
 * answer is sent when it closes, wherever the reader is by then.
 *
 * Held here, outside the Approval panel, so leaving the item does not take the answer with it: the
 * panel says what was done with Undo while it is on screen, and `ApprovalAnswerToast` (mounted once,
 * in `Layout.tsx`) says it once the panel is gone, and says a refusal that comes back after the reader
 * left, with a link back to where they answered. Closing the tab sends the held answer at once, with
 * `keepalive`, and says nothing.
 *
 * Not `DecisionAnswerToastStore` (IU-8): that one sends at the click and takes the answer back after;
 * this one sends nothing until the window closes.
 */
export const APPROVAL_UNDO_MS = 5_000;

export type ApprovalOutcomeType = 'confirmed' | 'declined';

/**
 * UXA-1: an answer of several sets (a guest's Approve all) that was taken for some and refused for
 * others. Thrown from `send` so the refusal is said, by the panel or the toast, in `sentence`; the
 * panel reads what was taken from `detail`.
 */
export class ApprovalAnswerPartlyTaken extends Error {
  constructor(
    readonly sentence: string,
    readonly detail: unknown,
  ) {
    super(sentence);
    this.name = 'ApprovalAnswerPartlyTaken';
  }
}

export interface IHeldApprovalAnswer {
  /** The panel that gave it: the item and set it answers. */
  readonly panelKey: string;
  readonly outcome: ApprovalOutcomeType;
  /** The set's label and the item's key, as the panel and the toast name them. */
  readonly label: string;
  readonly itemKey: string;
  /**
   * A customer's answer (§1.1): the toast names it by its design and never by the item's key, which
   * a customer does not read.
   */
  readonly guest?: boolean;
  /** Where the reader answered, so a refusal after they left links back there. */
  readonly backTo: string;
  /** Sends the answer, as the panel would have at the click. */
  readonly send: () => Promise<unknown>;
  /** Sends it as the page goes away (`keepalive`); nothing waits for it. */
  readonly sendOnLeave: () => void;
}

/** What a panel still on screen is told about its own answer. */
export interface IApprovalAnswerListener {
  sending(answer: IHeldApprovalAnswer): void;
  sent(answer: IHeldApprovalAnswer): void;
  refused(answer: IHeldApprovalAnswer, error: unknown): void;
}

/** A refusal that came back after the panel was gone: said in the toast, with the way back. */
export interface IApprovalAnswerRefusal {
  readonly answer: IHeldApprovalAnswer;
  readonly error: unknown;
}

export class ApprovalAnswerStore {
  held: IHeldApprovalAnswer | null = null;
  refusal: IApprovalAnswerRefusal | null = null;
  /** The panels on screen, by key: while one shows the held answer, the toast stays away. */
  attached = new Map<string, number>();

  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Map<string, IApprovalAnswerListener>();

  constructor() {
    makeObservable<ApprovalAnswerStore, 'clearTimer'>(this, {
      held: observable.ref,
      refusal: observable.ref,
      attached: observable.shallow,
      hold: action,
      undo: action,
      attach: action,
      dismissRefusal: action,
      leavePage: action,
      clearTimer: false,
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.leavePage());
    }
  }

  /** Holds an answer for `APPROVAL_UNDO_MS`. One held at a time: a second sends the first now. */
  hold(answer: IHeldApprovalAnswer): void {
    if (this.held) void this.flush();
    this.refusal = null;
    this.held = answer;
    this.timer = setTimeout(() => void this.flush(), APPROVAL_UNDO_MS);
  }

  /** Undo inside the window: the held answer goes, and nothing is sent. */
  undo(panelKey?: string): void {
    if (!this.held || (panelKey !== undefined && this.held.panelKey !== panelKey)) return;
    this.clearTimer();
    this.held = null;
  }

  isHeld(panelKey: string): boolean {
    return this.held?.panelKey === panelKey;
  }

  isAttached(panelKey: string): boolean {
    return (this.attached.get(panelKey) ?? 0) > 0;
  }

  /** A panel on screen hears about its own answer; the returned function lets go. */
  attach(panelKey: string, listener: IApprovalAnswerListener): () => void {
    this.listeners.set(panelKey, listener);
    this.attached.set(panelKey, (this.attached.get(panelKey) ?? 0) + 1);
    return () =>
      runInAction(() => {
        if (this.listeners.get(panelKey) === listener) this.listeners.delete(panelKey);
        const count = (this.attached.get(panelKey) ?? 1) - 1;
        if (count > 0) this.attached.set(panelKey, count);
        else this.attached.delete(panelKey);
      });
  }

  /** The window closed: send the held answer, and say how it went where the reader is. */
  async flush(): Promise<void> {
    const answer = this.held;
    if (!answer) return;
    runInAction(() => {
      this.clearTimer();
      this.held = null;
    });
    this.listeners.get(answer.panelKey)?.sending(answer);
    try {
      await answer.send();
      this.listeners.get(answer.panelKey)?.sent(answer);
    } catch (error) {
      const listener = this.listeners.get(answer.panelKey);
      if (listener) listener.refused(answer, error);
      else runInAction(() => (this.refusal = { answer, error }));
    }
  }

  dismissRefusal(): void {
    this.refusal = null;
  }

  /** The tab is closing: the held answer goes now, with `keepalive`, and nothing is said. */
  leavePage(): void {
    const answer = this.held;
    if (!answer) return;
    this.clearTimer();
    this.held = null;
    answer.sendOnLeave();
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Tests only. */
  resetForTests(): void {
    this.clearTimer();
    this.held = null;
    this.refusal = null;
    this.attached.clear();
    this.listeners.clear();
  }
}

export const approvalAnswerStore = new ApprovalAnswerStore();
